import React, { useMemo, useEffect, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import recordEvent from 'platform/monitoring/record-event';
import { focusElement } from 'platform/utilities/ui';
import {
  VaSidenav,
  VaSidenavItem,
} from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import _ from 'platform/utilities/data';

import { buildMajorSteps } from '../utils/buildMajorStepsFromConfig';
import { findHighImpactFieldEntryForPath } from '../utils/sideNav/highImpactFields';
import {
  trackSideNavChapterClick,
  trackMobileAccordionClick,
  trackSideNavReset,
  trackSideNavRestored,
} from '../utils/tracking/features/sideNavTracking';
import { isNewConditionsOn } from '../utils';
import { setPendingDestructiveNavigation } from '../../shared/actions/destructiveModal';
import { selectIsDestructiveActionPending } from '../../shared/selectors/destructiveModal';
import { DESTRUCTIVE_NAV_TRIGGER_ORIGINS } from '../../shared/constants';

const DISABLED_STYLE =
  'vads-u-margin--0 vads-u-padding-y--1 vads-u-padding-left--2 vads-u-padding-right--0p5 vads-u-color--black vads-u-border-color--gray-lightest vads-u-border-bottom--1px';

const PREVIOUS_STEP_STYLE =
  'vads-u-color--link vads-u-text-decoration--underline';

const applyHeaderAriaHidden = node => {
  node?.shadowRoot
    ?.querySelector('.va-sidenav__header')
    ?.setAttribute('aria-hidden', 'true');
};

/**
 * Side navigation component for the 526EZ disability claims form
 *
 * Displays major form chapters in a navigation menu and highlights the current chapter.
 * Tracks the user's progress through chapters using 'view:sideNavChapterIndex' to disable
 * future chapter links. Rebuilds navigation when save-in-progress data loads and updates
 * active state when navigating between pages.
 *
 * Note: This component is conditionally rendered based on screen size - only one instance
 * is mounted at a time (desktop sidebar OR mobile position below progress bar).
 *
 * @param {Object} props - Component props
 * @param {boolean} [props.enableAnalytics=false] - Whether to track navigation clicks in Google Analytics
 * @param {Object} props.formData - Current form data from Redux store, used to evaluate conditional pages
 * @param {string} props.pathname - Current URL pathname from react-router
 * @param {Object} props.router - React-router router object with push method
 * @param {Function} props.setFormData - Redux action to update form data
 * @returns {React.ReactElement} Side navigation component
 */
function ClaimFormSideNav({
  enableAnalytics = false,
  formData,
  pathname,
  router,
  setFormData,
}) {
  /**
   * Ref to access the VaSidenav shadow DOM for mobile accordion control
   */
  const sidenavRef = useRef(null);

  const dispatch = useDispatch();
  const isDestructiveActionPending = useSelector(
    selectIsDestructiveActionPending,
  );

  /**
   * Track previous accordion state to detect actual toggle events
   * Prevents tracking clicks on chapter items that bubble through
   */
  const previousAccordionStateRef = useRef(null);

  /**
   * Stores snapshot of a high-impact field's value when user is on a registered page.
   * @type {import('../utils/sideNav/highImpactFields').HighImpactFieldEntry|null}
   */
  const highImpactFieldRef = useRef(null);

  /**
   * Memoize major steps with formData and pathname dependencies
   * Rebuilds when save-in-progress loads or when navigating between pages
   * @type {import('../utils/buildMajorStepsFromConfig').MajorStep[]}
   */
  const landingPages = useMemo(
    () => buildMajorSteps(formData, pathname),
    [formData, pathname],
  );

  /**
   * Track the highest chapter index the user has reached
   * Used to disable navigation links for future chapters
   */
  const maxChapterIndex = formData['view:sideNavChapterIndex'] || 0;

  const setSidenavRef = useCallback(node => {
    sidenavRef.current = node;

    if (node) {
      const ready =
        typeof node.componentOnReady === 'function'
          ? node.componentOnReady()
          : Promise.resolve();

      ready.then(() => {
        applyHeaderAriaHidden(node);
        window.requestAnimationFrame(() => applyHeaderAriaHidden(node));
      });
    }
  }, []);

  /**
   * Snapshot management: when pathname changes, check if the new page is
   * in the high-impact field registry. If so, snapshot the current field
   * value for later comparison. Only creates a new snapshot when first
   * entering a field's workflow or switching to a different field.
   */
  useEffect(
    () => {
      const match = findHighImpactFieldEntryForPath(pathname);

      // Not on a registered page - ref persists for potential return navigation
      if (!match) {
        return;
      }

      // Only snapshot when first entering this field's workflow
      // Preserves snapshot when navigating between related pages (e.g., summary <-> new-condition)
      if (
        !highImpactFieldRef.current ||
        highImpactFieldRef.current.field !== match.field
      ) {
        highImpactFieldRef.current = {
          ...match,
          snapshot: _.get(match.field, formData),
          previousMaxChapterIndex: null,
        };
      }
    },

    // Only react to pathname changes, not formData, to avoid excessive snapshotting
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pathname],
  );

  /**
   * High-impact field comparison: when formData changes, compare current value
   * against snapshot. If an impactful change is detected (e.g., condition added/removed),
   * reset view:sideNavChapterIndex to prevent navigation to chapters with potentially
   * stale data. If the change is undone (e.g., via cancel), restore the previous chapter index.
   */
  useEffect(
    () => {
      const highImpactField = highImpactFieldRef.current;
      const currentChapterIndex =
        landingPages.find(page => page.current)?.idx ?? 0;

      if (!highImpactField) {
        return;
      }

      const currentValue = _.get(highImpactField.field, formData);
      const isImpactful = highImpactField.hasImpactfulChange(
        highImpactField.snapshot,
        currentValue,
      );

      const conditionsExperienceUsed = isNewConditionsOn(formData)
        ? 'v2'
        : 'v1';

      if (isImpactful) {
        const resetTo = Math.max(
          highImpactField.resetToChapter,
          currentChapterIndex,
        );

        if (resetTo < maxChapterIndex) {
          // Save original chapter index before first reset so it can be restored on undo
          if (highImpactField.previousMaxChapterIndex === null) {
            highImpactFieldRef.current.previousMaxChapterIndex =
              maxChapterIndex;
          }

          setFormData({
            ...formData,
            'view:sideNavChapterIndex': resetTo,
          });

          trackSideNavReset({
            pathname,
            fromChapterIndex: maxChapterIndex,
            toChapterIndex: resetTo,
            conditionsExperienceUsed,
          });
        }
      } else if (highImpactField.previousMaxChapterIndex !== null) {
        // Change was undone — restore the saved chapter index
        const restored = highImpactField.previousMaxChapterIndex;

        // Only call setFormData once - wait for Redux to update before clearing flag
        // This prevents race conditions with ArrayBuilder's cleanup on back navigation
        if (maxChapterIndex !== restored) {
          setFormData({
            ...formData,
            'view:sideNavChapterIndex': restored,
          });

          trackSideNavRestored({
            pathname,
            fromChapterIndex: maxChapterIndex,
            toChapterIndex: restored,
            conditionsExperienceUsed,
          });
        } else {
          // Already restored successfully, clear the flag
          highImpactFieldRef.current.previousMaxChapterIndex = null;
        }
      }
    },
    // Only react to formData changes to detect actual data modifications
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData],
  );

  /**
   * Update maxChapterIndex when user progresses to a new chapter
   */
  useEffect(
    () => {
      const currentChapter = landingPages.find(page => page.current);

      if (currentChapter?.idx > maxChapterIndex) {
        // We don't want any stale snapshots floating around
        highImpactFieldRef.current = null;
        setFormData({
          ...formData,
          'view:sideNavChapterIndex': currentChapter.idx,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [landingPages],
  );

  /**
   * Close mobile accordion when navigating between pages
   */
  useEffect(() => {
    if (sidenavRef.current) {
      const accordionItem = sidenavRef.current.shadowRoot?.querySelector(
        'va-accordion > va-accordion-item',
      );

      if (accordionItem) {
        // Save current scroll position before closing accordion
        const { scrollY } = window;

        accordionItem.removeAttribute('open');

        // Update ref to reflect closed state
        previousAccordionStateRef.current = false;

        // Restore scroll position to prevent accordion close from scrolling
        window.scrollTo(0, scrollY);
      }
    }
  }, [pathname]);

  /**
   * Hide visual sidenav header text from AT within shadow DOM.
   */
  useEffect(() => {
    applyHeaderAriaHidden(sidenavRef.current);
    const rafId = window.requestAnimationFrame(() => {
      applyHeaderAriaHidden(sidenavRef.current);
    });

    return () => {
      window.cancelAnimationFrame(rafId);
    };
  }, [pathname]);

  /**
   * Handle clicks on sidenav to track accordion expand/collapse
   * Only tracks when accordion state actually changes to avoid tracking
   * chapter navigation clicks that bubble through
   */
  const handleSidenavClick = () => {
    // Defer to next tick so web component's click handler updates aria-expanded first
    setTimeout(() => {
      const accordionItem = sidenavRef.current?.shadowRoot?.querySelector(
        'va-accordion > va-accordion-item',
      );

      if (!accordionItem) return;

      const accordionButton = accordionItem.shadowRoot?.querySelector(
        'button[aria-expanded]',
      );

      if (!accordionButton) return;

      const isExpanded =
        accordionButton.getAttribute('aria-expanded') === 'true';

      // Only track if the state actually changed
      if (previousAccordionStateRef.current !== isExpanded) {
        const state = isExpanded ? 'expanded' : 'collapsed';
        const accordionTitle = accordionButton.textContent?.trim();

        trackMobileAccordionClick({
          pathname,
          state,
          accordionTitle,
        });

        // Update the ref with the new state
        previousAccordionStateRef.current = isExpanded;
      }
    }, 0);
  };

  /**
   * Handle navigation item click
   * Tracks analytics if enabled and navigates to the selected chapter.
   * If a destructive action is pending on the current page, defers
   * navigation and lets the page's destructive modal handle confirmation.
   * @param {Event} e - Click event
   * @param {Object} pageData - Page data including key, label, and path
   */
  function handleClick(e, pageData) {
    e.preventDefault();
    const destination = pageData.path;
    if (enableAnalytics) {
      recordEvent?.({
        event: 'form-sidenav-click',
        'form-sidenav-pageData-key': pageData.key,
        'form-sidenav-pageData-label': pageData.label,
        'form-sidenav-destination-path': destination,
      });
    }
    // Track side nav click for DataDog RUM
    trackSideNavChapterClick({
      pageData,
      pathname,
    });

    if (destination === pathname) {
      // Same page click — manually focus the h3 for accessibility
      focusElement('h3');
      return;
    }

    // If a destructive action is pending, defer navigation to the
    // page's destructive modal instead of navigating immediately.
    if (isDestructiveActionPending) {
      dispatch(
        setPendingDestructiveNavigation({
          url: destination,
          origin: DESTRUCTIVE_NAV_TRIGGER_ORIGINS.SIDENAV,
        }),
      );
      return;
    }

    // We don't want any stale snapshots floating around
    highImpactFieldRef.current = null;
    setFormData(formData);

    router.push(destination);
  }

  return (
    <div className="claim-form-sidenav-wrapper">
      <VaSidenav
        header="Form steps"
        icon-background-color="vads-color-link"
        icon-name="description"
        id="default-sidenav"
        mobileHeader="Select a step"
        // Assistive technology already adds a "navigation" qualifier; listing only what the label applies to reduces
        // redundancy and improves clarity. See https://va.ghe.com/software/va.gov-team/issues/138016
        navAriaLabel="Form steps"
        ref={setSidenavRef}
        onClick={handleSidenavClick}
      >
        {landingPages.map((page, index) => {
          const label = `Step ${index + 1}: ${page.label}`;
          if (page.current) {
            return (
              <VaSidenavItem
                key={page.key}
                label={label}
                href="#"
                current-page
                data-page={page.key}
                onClick={e => handleClick(e, page)}
              />
            );
          }

          // page.idx instead of index for optional chapters that may
          //  be included after being skipped (see CFI flow)
          return page.idx <= maxChapterIndex ? (
            <VaSidenavItem
              key={page.key}
              label={label}
              href="#"
              data-page={page.key}
              className={PREVIOUS_STEP_STYLE}
              onClick={e => handleClick(e, page)}
            />
          ) : (
            <p role="listitem" className={DISABLED_STYLE} key={page.key}>
              <span className="sr-only">{`Complete previous section first. ${label}`}</span>
              <span aria-hidden="true">{label}</span>
            </p>
          );
        })}
      </VaSidenav>
    </div>
  );
}

ClaimFormSideNav.propTypes = {
  enableAnalytics: PropTypes.bool,
  formData: PropTypes.object,
  pathname: PropTypes.string,
  router: PropTypes.object,
  setFormData: PropTypes.func,
  shouldHide: PropTypes.bool,
};

// Memoize to prevent unnecessary re-renders when props haven't changed
export default React.memo(ClaimFormSideNav);
